/**
 * Encounter schemas — billable session encounters.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type {
  EncounterStatus as FhirEncounterStatus,
  EncounterCancellationReasonCode as FhirCancellationReasonCode,
} from '@unify/fhir-model/resources/encounter';

/** FHIR Encounter.status values (Unify 3-state model) — synced with fhir-model. */
export const encounterStatusValues = [
  'planned',
  'finished',
  'cancelled',
] as const satisfies readonly FhirEncounterStatus[];

/** Cancellation reason codes — synced with fhir-model. */
export const cancellationReasonCodeValues = [
  'noshow',
  'client-cancelled',
  'client-late-cancelled',
  'clinician-cancelled',
] as const satisfies readonly FhirCancellationReasonCode[];

/**
 * Update an Encounter's status (e.g. mark cancelled with reason).
 */
export const encounterStatusUpdateSchema = z.object({
  encounterId: z.string().min(1, 'Encounter ID is required'),
  status: z.enum(encounterStatusValues),
  cancellationReason: z.enum(cancellationReasonCodeValues).optional(),
}).refine(
  (d) => d.status !== 'cancelled' || !!d.cancellationReason,
  { message: 'Cancellation reason is required when status is cancelled', path: ['cancellationReason'] },
);

export type EncounterStatusUpdateInput = z.infer<typeof encounterStatusUpdateSchema>;
